import React from 'react';
import { getNestedValue, getColorClasses } from './utils';

/**
 * TimelineWidget - Renders a vertical timeline of events
 * 
 * Config (from config_json):
 * {
 *   "type": "timeline",
 *   "options": {
 *     "dataKey": "brokenLogData",             // Key in enrichedData containing the array
 *     "maxItems": 5,                           // Max visible items
 *     "dateKey": "broken_date",                // Key within each item for date
 *     "titleKey": "broken_description",        // Key within each item for title
 *     "descriptionKey": "maintenance_description", // Key for description (optional)
 *     "color": "red",                          // Timeline dot/line color
 *     "emptyMessage": "이력이 없습니다",        // Message when no data
 *     "title": "최근 사고 이력"                 // Optional header
 *   },
 *   "items": []
 * }
 */
export default function TimelineWidget({ config, data }) {
  if (!config) return null;

  const {
    dataKey,
    maxItems = 5,
    dateKey = 'date',
    titleKey = 'title',
    descriptionKey = 'description',
    color = 'red',
    emptyMessage = '이력이 없습니다',
  } = config;

  const colors = getColorClasses(color);

  // Get timeline data from enrichedData
  const rawData = getNestedValue(data, dataKey, []);
  const timelineData = Array.isArray(rawData) ? rawData : [];
  const displayItems = maxItems > 0 ? timelineData.slice(0, maxItems) : timelineData;

  return (
    <div>
      {config.title && (
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-[12px] text-slate-400 font-bold">{config.title}</span>
          {timelineData.length > maxItems && maxItems > 0 && (
            <span className="text-[10px] text-slate-600 font-bold">
              {maxItems}/{timelineData.length}건
            </span>
          )}
        </div>
      )}

      {displayItems.length === 0 ? (
        <div
          className="flex items-center justify-center py-6 rounded-2xl bg-white/5 text-slate-600 text-[11px] font-bold"
          style={{ border: '1px solid rgba(255,255,255,0.04)' }}
        >
          {emptyMessage}
        </div>
      ) : (
        <div className="relative pl-6">
          {/* Vertical line */}
          <div
            className="absolute left-[7px] top-2 bottom-2 w-[2px]"
            style={{ background: `linear-gradient(to bottom, ${colors.border}, transparent)` }}
          />

          {displayItems.map((item, idx) => {
            const date = item[dateKey] || '-';
            const title = item[titleKey] || '-';
            const description = item[descriptionKey];
            const isLast = idx === displayItems.length - 1;

            return (
              <div key={idx} className={`relative ${isLast ? '' : 'pb-4'}`}>
                {/* Dot */}
                <div
                  className={`absolute -left-6 top-1.5 w-4 h-4 rounded-full flex items-center justify-center`}
                  style={{ background: 'rgba(10,15,30,0.9)' }}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${colors.dot}`}
                    style={{
                      boxShadow: idx === 0 ? `0 0 8px ${colors.border}` : 'none',
                    }}
                  />
                </div>

                {/* Content */}
                <div
                  className="rounded-xl px-3 py-2.5 bg-white/5 hover:bg-white/8 transition-colors"
                  style={{ border: '1px solid rgba(255,255,255,0.04)' }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-slate-200 font-bold leading-tight line-clamp-1">
                      {title}
                    </span>
                    <span className="text-[9px] text-slate-600 font-mono tracking-tight ml-2 shrink-0">
                      {date}
                    </span>
                  </div>
                  {description && (
                    <p className="text-[10px] text-slate-500 font-medium leading-relaxed line-clamp-2 m-0">
                      {description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
